import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaList, FaChartPie, FaHome, FaTh, FaBox, FaTags, FaSignOutAlt, FaChevronRight, FaBars } from "react-icons/fa"; 
import { AuthContext } from "../components/AuthContext";
import { API_URL } from "../constant";

const Subcategories = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({
    id: null,
    categoryId: "",
    subcategoryName: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [showAddCard, setShowAddCard] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const refreshSubcategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/categories/subcategories/all`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch subcategories");
      }
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  }, [authToken]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [authToken]);

  // Fetch subcategories from API on component mount
  useEffect(() => {
    refreshSubcategories();
  }, [authToken, refreshSubcategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle adding a new subcategory via API POST
  const handleAdd = () => {
    const { categoryId, subcategoryName } = formData;
    if (!categoryId || !subcategoryName) {
      setModalMessage("Category and Sub-Category Name are required.");
      setModalType("error");
      setShowModal(true);
      return;
    }
    fetch(`${API_URL}/api/category/subcategories`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        category_id: categoryId,
        subcategory_name: subcategoryName,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to add subcategory");
        }
        return response.json();
      })
      .then((newSubcategory) => {
        // Re-fetch the updated subcategories from backend
        refreshSubcategories();
        setFormData({ id: null, categoryId: "", subcategoryName: "" });
        setShowAddCard(false);
        setModalMessage("Subcategory added successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error adding subcategory:", error);
        setModalMessage("Error adding subcategory");
        setModalType("error");
        setShowModal(true);
      });
  };

  // Handle editing a subcategory (open modal with pre-filled data)
  const handleEdit = (subcategory) => {
    setEditingId(subcategory.id);
    setFormData({
      id: subcategory.id,
      categoryId: subcategory.category_id,
      subcategoryName: subcategory.subcategory_name,
    });
    setShowAddCard(true);
  };

  // Handle updating an existing subcategory via API PUT
  const handleUpdate = () => {
    if (editingId !== null) {
      fetch(
        `${API_URL}/api/categories/${formData.categoryId}/subcategories/${formData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            category_id: formData.categoryId,
            subcategory_name: formData.subcategoryName,
          }),
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to update subcategory");
          }
          return response.json();
        })
        .then((updatedSubcategory) => {
          // Re-fetch updated subcategories from backend
          refreshSubcategories();
          setEditingId(null);
          setFormData({ id: null, categoryId: "", subcategoryName: "" });
          setShowAddCard(false);
          setModalMessage("Subcategory updated successfully!");
          setModalType("success");
          setShowModal(true);
        })
        .catch((error) => {
          console.error("Error updating subcategory:", error);
          setModalMessage("Error updating subcategory");
          setModalType("error");
          setShowModal(true);
        });
    }
  };

  // Handle deleting a subcategory
  const handleDelete = (subcategory) => {
    setSubcategoryToDelete(subcategory);
    setShowDeleteModal(true);
  };

  // Confirm deletion via API DELETE
  const confirmDeleteSubcategory = () => {
    fetch(
      `${API_URL}/api/categories/${subcategoryToDelete.category_id}/subcategories/${subcategoryToDelete.id}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Error deleting subcategory");
        }
        // Re-fetch updated subcategories from backend
        refreshSubcategories();
        setShowDeleteModal(false);
        setModalMessage("Subcategory deleted successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error deleting subcategory:", error);
        setModalMessage("Error deleting subcategory");
        setModalType("error");
        setShowModal(true);
      });
  };

  const cancelDelete = () => setShowDeleteModal(false);

  const filteredSubcategories = subcategories.filter((sub) => {
    const categoryName = sub.category_name
      ? sub.category_name.toLowerCase()
      : "";
    const subcategoryName = sub.subcategory_name
      ? sub.subcategory_name.toLowerCase()
      : "";
    const searchTerm = search.toLowerCase();
    return categoryName.includes(searchTerm) || subcategoryName.includes(searchTerm);
  });

  const totalPages = Math.ceil(filteredSubcategories.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubcategories.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white shadow-md">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars className="text-2xl text-gray-600" />
        </button>
        <span className="text-xl font-bold text-gray-700">Subcategories</span>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen overflow-hidden">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "block" : "hidden"} md:block w-full md:w-64 bg-white shadow-md flex flex-col`}>
          <div className="hidden md:flex items-center justify-center py-6">
            <div className="text-3xl font-bold text-gray-600 flex items-center">
              <FaChartPie style={{ fontSize: "48px", marginRight: "10px" }} />
              <span className="text-2xl md:text-3xl font-bold" style={{ color: "gray-600" }}>
                Dashboard
              </span>
            </div>
          </div>
          {/* Top Section */}
          <nav className="mt-6 flex flex-col flex-grow text-gray-600">
            <ul>
              {[
                { name: "Home", icon: <FaHome className="mr-2" style={{ fontSize: "20px" }} />, link: "/Dashboard" },
                { name: "Categories", icon: <FaList className="mr-2" style={{ fontSize: "20px" }} />, link: "/Categories" },
                { name: "Subcategories", icon: <FaTh className="mr-2" style={{ fontSize: "20px" }} />, link: "/Subcategories"},
                { name: "Products", icon: <FaBox className="mr-2" style={{ fontSize: "20px" }} />, link: "/Products"},
                { name: "Attributes", icon: <FaTags className="mr-2" style={{ fontSize: "20px" }} />, link: "/Attributes"},
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate(item.link)}
                  className={`cursor-pointer px-6 py-2 rounded-lg flex items-center justify-between text-lg ${
                    item.name === "Subcategories"
                      ? "bg-gray-200 scale-90"
                      : "hover:bg-gray-200 hover:scale-90"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <FaChevronRight className="text-gray-600" />
                </li>
              ))}
            </ul>
          </nav>
          {/* Bottom Section */}
          <div className="mt-auto flex flex-col text-red-600">
            <ul>
              {[
                { name: "Signout", icon: (<FaSignOutAlt className="mr-2"style={{ fontSize: "20px" }}/>),},
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate("/Logout")}
                  className="cursor-pointer px-6 py-2 hover:bg-gray-200 rounded-lg flex items-center justify-between text-lg"
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <FaTh className="text-gray-700 mr-2 text-2xl" />
              <span className="text-2xl font-bold text-gray-700">
                Subcategories
              </span>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <button onClick={() => setShowAddCard(true)} className="bg-gray-500 text-white py-2 px-6 rounded hover:bg-gray-600 whitespace-nowrap">
                Add Subcategory
              </button>
              <div className="w-full md:w-64">
                <input
                  placeholder="Search..."
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-gray-400 focus:ring-gray-500 rounded p-2 w-full"
                />
              </div>
            </div>
          </div>

          {/* Global Modal for messages */}
          {showModal && (
            <div className="fixed inset-0 z-[1000] bg-gray-400 bg-opacity-50 flex items-center justify-center" onClick={() => setShowModal(false)}>
              <div className="bg-white p-6 rounded shadow-md w-80" onClick={(e) => e.stopPropagation()}>
                <p className={`text-center ${modalType === "success" ? "text-black" : "text-red-500"}`}>
                  {modalMessage}
                </p>
                <div className="mt-4 text-center">
                  <button onClick={() => setShowModal(false)} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                    {modalType === "success" ? "Done" : "Close"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p> {" "}Are you sure you want to delete "{subcategoryToDelete?.subcategory_name}"?{" "}</p>
                <div className="flex justify-end gap-4 mt-4">
                  <button onClick={confirmDeleteSubcategory} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                    Delete
                  </button>
                  <button onClick={cancelDelete} className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Subcategory Modal */}
          {showAddCard && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded shadow-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">
                  {editingId !== null ? "Edit Sub-Category" : "Add Subcategory"}
                </h2>
                <select name="categoryId" value={formData.categoryId} onChange={handleInputChange} className="border border-gray-400 rounded p-2 w-full mb-4">
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Subcategory Name"
                  type="text"
                  name="subcategoryName"
                  value={formData.subcategoryName}
                  onChange={handleInputChange}
                  className="border border-gray-400 rounded p-2 w-full mb-4"
                />
                <div className="flex justify-end gap-4">
                  <button onClick={() => setShowAddCard(false)} className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
                    Cancel
                  </button>
                  <button onClick={editingId !== null ? handleUpdate : handleAdd} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                    {editingId !== null ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subcategories Table */}
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse border border-gray-400">
              <thead>
                <tr>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">ID</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Category</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Subcategory</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((subcategory) => (
                  <tr key={subcategory.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 md:px-10 py-2 text-gray-700">{subcategory.id}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">{categories.find((cat) => cat.id === subcategory.category_id)?.category_name || "Unknown"}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">{subcategory.subcategory_name}</td>
                    <td className="px-4 md:px-10 py-2">
                      <button onClick={() => handleEdit(subcategory)} className="bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 mr-2">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(subcategory)} className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex justify-center items-center gap-4">
            <button className={`px-4 py-2 rounded-md ${
                currentPage === 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            <span className="text-black">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button className={`px-4 py-2 rounded-md ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }>
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Subcategories;
